import React from 'react';

// a thin band across the middle of the viewport, so a section becomes active as it crosses the
// centre rather than the moment its top edge appears
const MIDDLE_BAND = '-40% 0px -40% 0px';

/**
 * Tracks which of `sectionIds` is currently crossing the middle of the viewport.
 *
 * `sectionIds` must be referentially stable (a module-level constant, or memoised) — it is an
 * effect dependency, so a fresh array each render would rebuild the observer every render.
 */
const useScrollSpy = (sectionIds: string[]) => {
    const [activeId, setActiveId] = React.useState(sectionIds[0]);

    React.useEffect(() => {
        const elements = sectionIds
            .map((id) => document.getElementById(id))
            .filter((element): element is HTMLElement => element !== null);

        const inBand = new Set<string>();

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        inBand.add(entry.target.id);
                    } else {
                        inBand.delete(entry.target.id);
                    }
                }

                // resolved in document order so the result never depends on which entries fired
                // first; an empty band (mid-gap between sections) leaves the previous item lit
                const next = sectionIds.find((id) => inBand.has(id));

                if (next) {
                    setActiveId(next);
                }
            },
            { rootMargin: MIDDLE_BAND }
        );

        for (const element of elements) {
            observer.observe(element);
        }

        return () => observer.disconnect();
    }, [sectionIds]);

    return activeId;
};

export default useScrollSpy;
